import React, { useContext, useEffect, useCallback, useRef } from 'react'
import gsap from 'gsap'
import PropTypes from 'prop-types'
import { StoreState } from '../../Store'

// SVGs
import ExpeditionRouteCircle from '../svgs/ExpeditionRoutes/border'
import ExpeditionRouteMarker from '../svgs/ExpeditionRoutes/marker'
import ExpeditionRoute1 from '../svgs/ExpeditionRoutes/2/1'
import ExpeditionRoute2 from '../svgs/ExpeditionRoutes/2/2'
import ExpeditionRoute3 from '../svgs/ExpeditionRoutes/2/3'
import ExpeditionRoute4 from '../svgs/ExpeditionRoutes/2/4'

// Styles
import {
  SingleRouteMain,
  SingleRoutePath,
  SingleRouteLabel,
  SingleRouteLocation,
  SingleRoutePhoto,
} from './index.style'

// Images
import BarrierReef from '../Map/assets/locations/great-barrier-reef.jpg'
import Wall from '../Map/assets/locations/great-wall.jpg'
import TajMahal from '../Map/assets/locations/taj-mahal.jpg'

const Route2 = ({ id, backgroundColor, labelColor }) => {
  const store = useContext(StoreState)
  const { waiting } = store

  const routeIn = useRef()

  const line1Ref = {
      line: useRef(),
      arrow: useRef(),
    },
    line2Ref = {
      line: useRef(),
      arrow: useRef(),
    },
    line3Ref = {
      line: useRef(),
      arrow: useRef(),
    },
    line4Ref = {
      line: useRef(),
    },
    circle1Ref = useRef(),
    circle2Ref = useRef(),
    circle3Ref = useRef(),
    photo1Ref = useRef(),
    photo2Ref = useRef(),
    photo3Ref = useRef(),
    label1Ref = useRef(),
    label2Ref = useRef(),
    label3Ref = useRef()

  const play = useCallback(() => {
    routeIn.current.play(0)
  }, [routeIn])

  useEffect(() => {
    if (!waiting) setTimeout(play, 500)
  }, [waiting, play])

  useEffect(() => {
    if (routeIn.current) return

    routeIn.current = gsap.timeline({
      paused: true,
    })

    routeIn.current.to(line1Ref.line.current, {
      ease: 'power1.inOut',
      strokeDashoffset: 294,
      duration: 1.2,
    })

    routeIn.current.to(
      line1Ref.arrow.current,
      {
        ease: 'power2.out',
        opacity: 1,
        duration: 0.4,
      },
      '=-.3'
    )

    routeIn.current.addLabel('location1', '-=.5')

    routeIn.current.to(
      circle1Ref.current,
      {
        ease: 'power2.inOut',
        strokeDashoffset: 0,
        duration: 1.5,
      },
      'location1'
    )

    routeIn.current.to(
      photo1Ref.current,
      {
        ease: 'power1.inOut',
        opacity: 1,
        duration: 1,
      },
      'location1+=.5'
    )

    routeIn.current.to(
      label1Ref.current,
      {
        ease: 'power2.out',
        opacity: 1,
        y: 0,
        duration: 0.8,
      },
      'location1+=.7'
    )

    // Line 2

    routeIn.current.to(
      line2Ref.line.current,
      {
        ease: 'power1.inOut',
        strokeDashoffset: 595,
        duration: 1.9,
      },
      '=-.6'
    )

    routeIn.current.to(
      line2Ref.arrow.current,
      {
        ease: 'power2.out',
        opacity: 1,
        duration: 0.4,
      },
      '=-.3'
    )

    routeIn.current.addLabel('location2', '-=.5')

    routeIn.current.to(
      circle2Ref.current,
      {
        ease: 'power2.inOut',
        strokeDashoffset: 0,
        duration: 1.5,
      },
      'location2'
    )

    routeIn.current.to(
      photo2Ref.current,
      {
        ease: 'power1.inOut',
        opacity: 1,
        duration: 1,
      },
      'location2+=.5'
    )

    routeIn.current.to(
      label2Ref.current,
      {
        ease: 'power2.out',
        opacity: 1,
        y: 0,
        duration: 0.8,
      },
      'location2+=.7'
    )

    // Line 3

    routeIn.current.to(
      line3Ref.line.current,
      {
        ease: 'power1.inOut',
        strokeDashoffset: 730,
        duration: 2.4,
      },
      '=-.6'
    )

    routeIn.current.to(
      line3Ref.arrow.current,
      {
        ease: 'power2.out',
        opacity: 1,
        duration: 0.4,
      },
      '=-.3'
    )

    routeIn.current.addLabel('location3', '-=.5')

    routeIn.current.to(
      circle3Ref.current,
      {
        ease: 'power2.inOut',
        strokeDashoffset: 0,
        duration: 1.5,
      },
      'location3'
    )

    routeIn.current.to(
      photo3Ref.current,
      {
        ease: 'power1.inOut',
        opacity: 1,
        duration: 1,
      },
      'location3+=.5'
    )

    routeIn.current.to(
      label3Ref.current,
      {
        ease: 'power2.out',
        opacity: 1,
        y: 0,
        duration: 0.8,
      },
      'location3+=.7'
    )

    // Line 4

    routeIn.current.to(
      line4Ref.line.current,
      {
        ease: 'power1.inOut',
        strokeDashoffset: 425,
        duration: 1.5,
      },
      '=-.6'
    )
  }, [
    routeIn,
    line1Ref.line,
    line1Ref.arrow,
    line2Ref.line,
    line2Ref.arrow,
    line3Ref.line,
    line3Ref.arrow,
    line4Ref.line,
    line4Ref.arrow,
    circle1Ref,
    circle2Ref,
    circle3Ref,
    photo1Ref,
    photo2Ref,
    photo3Ref,
    label1Ref,
    label2Ref,
    label3Ref,
  ])

  return (
    <SingleRouteMain>
      <SingleRoutePath right={0} top={74.4} width={16.9}>
        <ExpeditionRoute1
          forwardRef={line1Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
      <SingleRouteLocation right={8.5} top={67.2}>
        <SingleRouteLabel ref={label1Ref} left={true} color={labelColor}>
          Great Barrier Reef <ExpeditionRouteMarker fill={labelColor} />
        </SingleRouteLabel>
        <ExpeditionRouteCircle
          forwardRef={circle1Ref}
          stroke={labelColor}
          rotation={70}
        />
        <SingleRoutePhoto ref={photo1Ref} bgImage={BarrierReef} />
      </SingleRouteLocation>
      <SingleRoutePath right={14.2} top={27.7} width={34.2} zIndex={2}>
        <ExpeditionRoute2
          forwardRef={line2Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
      <SingleRouteLocation right={26.5} top={21.9}>
        <SingleRouteLabel ref={label2Ref} left={true} color={labelColor}>
          Great Wall of China <ExpeditionRouteMarker fill={labelColor} />
        </SingleRouteLabel>
        <ExpeditionRouteCircle
          forwardRef={circle2Ref}
          stroke={labelColor}
          rotation={140}
        />
        <SingleRoutePhoto ref={photo2Ref} bgImage={Wall} />
      </SingleRouteLocation>
      <SingleRoutePath right={16} top={21.7} width={59.9}>
        <ExpeditionRoute3
          forwardRef={line3Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
      <SingleRouteLocation right={74.7} top={36.9}>
        <SingleRouteLabel ref={label3Ref} right={true} color={labelColor}>
          <ExpeditionRouteMarker fill={labelColor} /> The Taj Mahal
        </SingleRouteLabel>
        <ExpeditionRouteCircle
          forwardRef={circle3Ref}
          stroke={labelColor}
          rotation={50}
        />
        <SingleRoutePhoto ref={photo3Ref} bgImage={TajMahal} />
      </SingleRouteLocation>
      <SingleRoutePath right={80.5} top={0} width={19.3}>
        <ExpeditionRoute4
          forwardRef={line4Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
    </SingleRouteMain>
  )
}

Route2.propTypes = {
  id: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
  labelColor: PropTypes.string,
}

export default Route2
